import React from "react";
import { useTranslation } from "react-i18next";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import { trackSectionViewed, useSectionOnceObserver } from "../../lib/analytics";
import { WPS_SECTION } from "./sections.js";
import useLocalizedHref from "../../hooks/useLocalizedHref.js";

/**
 * Before / During / After — photo sharing placed inside the wider Agapaé arc.
 *
 * Reuses the home collection grid (`home-col-list` / `home-col-item` /
 * `col-im-link` / `card-im`), which already falls to a single column at 767px,
 * with real photography from `public/assets/images`.
 *
 * Before / During / After is a genuine chronology, so the labels carry meaning
 * rather than decorating the cards — which is also why they are rendered as the
 * card's first line and not as arbitrary numbering.
 *
 * "Before" describes the digital invitation, so the whole card is that page's
 * link. The anchor replaces the `<article>` rather than wrapping its children:
 * `.home-col-item` is the flex column, so keeping it on the same box leaves the
 * layout untouched, and the global `a { color: var(--black); text-decoration:
 * none }` means the card looks exactly as it did.
 */
const CARDS = [
  {
    key: "before",
    image: "/assets/images/invitation/invitation-1.jpg",
    href: "/digital-wedding-invitation",
  },
  { key: "during", image: "/assets/images/moments/moment_2.jpg" },
  { key: "after", image: "/assets/images/moments/moment_3.jpg" },
];

const PhotoSharingArc = () => {
  const { t } = useTranslation();
  const localize = useLocalizedHref();

  const analyticsContext = useAnalyticsContext();

  // One impression the first time the section scrolls into view; the observer
  // disconnects itself afterwards.
  const sectionRef = useSectionOnceObserver(() =>
    trackSectionViewed(WPS_SECTION.ARC, analyticsContext),
  );

  return (
    <section ref={sectionRef} className="section topless wps-section">
      <div className="container">
        <div className="wps-section-header">
          <h2 className="heading-136">{t("weddingPhotoSharing.arc.heading")}</h2>
        </div>

        <div className="home-col-wrapper wps-cards">
          <div className="home-col-list">
            {CARDS.map(({ key, image, href }) => {
              const Card = href ? "a" : "article";
              return (
              <Card
                key={key}
                {...(href ? { href: localize(href) } : {})}
                className="home-col-item wps-card"
              >
                <div className="col-im-link">
                  <img
                    src={image}
                    alt={t(`weddingPhotoSharing.arc.cards.${key}.imageAlt`)}
                    loading="lazy"
                    className="wps-card-im"
                  />
                </div>
                <p className="wps-eyebrow wps-card-label">
                  {t(`weddingPhotoSharing.arc.cards.${key}.label`)}
                </p>
                <h3 className="made-22 wps-card-title">
                  {t(`weddingPhotoSharing.arc.cards.${key}.title`)}
                </h3>
                <p className="body-15 wps-card-body">
                  {t(`weddingPhotoSharing.arc.cards.${key}.body`)}
                </p>
              </Card>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PhotoSharingArc;
